/**
 * Single source of truth for the ToxicDetector pricing system.
 * Every pricing surface (landing section, onboarding, upload page, settings
 * billing) renders from this data so the plans stay one coherent system.
 *
 * Payment processing is intentionally NOT defined here — paid plans expose
 * their checkout identifier only, which is handed to `onCheckout(plan)`.
 * The four checkout identifiers below are the exact values the payment
 * Edge Function accepts.
 */
export type CheckoutPlanId = 'single_scan' | 'weekly' | 'monthly' | 'yearly'
export type PricingPlanId = 'free' | CheckoutPlanId
export type PlanAccent = 'neutral' | 'gold' | 'red'
/**
 * Visual weight inside the pricing system.
 * lead      — the free entry point, rendered as a lighter lead-in strip
 * standard  — baseline paid card
 * secondary — second strongest premium card (Yearly)
 * primary   — dominant recommended card (Monthly)
 */
export type PlanEmphasis = 'lead' | 'standard' | 'secondary' | 'primary'
export interface PricingPlan {
  id: PricingPlanId
  /** Marketing name, e.g. "Pay Per Scan". */
  name: string
  /** Uppercase system label used on authenticated status surfaces. */
  label: string
  glyph: string
  price: string
  /** Price with its interval, e.g. "$29.99 / month". */
  priceLabel: string
  /** Billing interval on its own, e.g. "Monthly". */
  billing: string
  /** One-line scan entitlement this plan grants. */
  entitlement: string
  /** Very short line for compact plan cards. */
  summary: string
  /**
   * Undiscounted reference price, shown struck through beside the price.
   * Only set where a real comparison exists (Yearly vs. 12× Monthly).
   */
  originalPrice?: string
  /** Short savings chip, e.g. "Save 58%". */
  savings?: string
  cadence: string
  features: string[]
  cta: string
  badge?: string
  note?: string
  accent: PlanAccent
  emphasis: PlanEmphasis
}
export const freePlan: PricingPlan = {
  id: 'free',
  name: 'Just Curious',
  label: 'FREE',
  glyph: '🆓',
  price: '$0',
  priceLabel: '$0',
  billing: 'Forever',
  entitlement: '1 free forensic scan',
  summary: '1 free scan',
  cadence: 'Forever',
  features: [
    '1 free forensic scan',
    'Basic toxicity detection',
    'Teaser results card',
    'Standard processing speed',
  ],
  cta: 'Start free scan',
  accent: 'neutral',
  emphasis: 'lead',
}
export const paidPlans: PricingPlan[] = [
  {
    id: 'single_scan',
    name: 'Pay Per Scan',
    label: 'SINGLE SCAN',
    glyph: '🔥',
    price: '$2.99',
    priceLabel: '$2.99 / scan',
    billing: 'Per scan',
    entitlement: '1 full forensic scan',
    summary: 'One full report',
    cadence: 'Per scan',
    features: [
      '1 full forensic scan',
      'Full AI insights & analysis',
      'All advanced analysis modes',
      'Detailed pattern labels',
      'Personalized advice',
      'Shareable results',
      'PDF report',
    ],
    cta: 'Buy a single scan',
    accent: 'neutral',
    emphasis: 'standard',
  },
  {
    id: 'weekly',
    name: 'Weekly',
    label: 'WEEKLY',
    glyph: '⚡',
    price: '$9.99',
    priceLabel: '$9.99 / week',
    billing: 'Weekly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per week',
    features: [
      'Unlimited forensic scans while active',
      'Advanced analysis modes',
      'Detailed pattern detection',
      'Priority processing',
      'Weekly relationship insights',
      'Shareable results',
      'PDF reports',
    ],
    cta: 'Start weekly',
    accent: 'neutral',
    emphasis: 'standard',
  },
  {
    id: 'monthly',
    name: 'Monthly',
    label: 'MONTHLY',
    glyph: '⭐',
    price: '$29.99',
    priceLabel: '$29.99 / month',
    billing: 'Monthly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per month',
    badge: 'Most popular',
    features: [
      'Unlimited forensic scans',
      'Persona evolution tracking',
      'Monthly clarity reports',
      'Gaslight alert system',
      'Priority processing queue',
      'Full PDF case files',
      'All advanced analysis modes',
    ],
    cta: 'Get monthly access',
    accent: 'red',
    emphasis: 'primary',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    label: 'YEARLY',
    glyph: '💎',
    price: '$149.99',
    priceLabel: '$149.99 / year',
    billing: 'Yearly',
    entitlement: 'Unlimited forensic scans',
    summary: 'Unlimited scans',
    cadence: 'Per year',
    // 12 × $29.99 = $359.88 a year on Monthly.
    originalPrice: '$359.88',
    savings: 'Save 58%',
    badge: 'Best value',
    note: 'Everything in Monthly. Paying $29.99 every month costs $359.88 a year — you keep $209.89.',
    features: [
      'Unlimited forensic scans',
      'Persona evolution tracking',
      'Monthly clarity reports',
      'Gaslight alert system',
      'Priority processing queue',
      'Full PDF case files',
      'All advanced analysis modes',
    ],
    cta: 'Get yearly access',
    accent: 'gold',
    emphasis: 'secondary',
  },
]
export const pricingPlans: PricingPlan[] = [freePlan, ...paidPlans]
/** Entry tiers: try it once, or pay for a single report. */
export const entryPlans: PricingPlan[] = [
  freePlan,
  paidPlans.find((plan) => plan.id === 'single_scan') as PricingPlan,
]
/** Unlimited access tiers, ordered weekly → monthly → yearly. */
export const subscriptionPlans: PricingPlan[] = paidPlans.filter((plan) =>
  ['weekly', 'monthly', 'yearly'].includes(plan.id),
)
export function getPlan(id: PricingPlanId): PricingPlan | undefined {
  return pricingPlans.find((plan) => plan.id === id)
}
/** Uppercase system label for a plan, safe for unknown values. */
export function planLabel(id: PricingPlanId): string {
  return getPlan(id)?.label ?? 'FREE'
}
